import { ContainerHeight } from '@planning/app/api'
import { INonNumericReleaseItem } from '@planning/rt-stores/nnrOrder/NnrOrderItem'
import { INumericReleaseItem } from '@planning/rt-stores/numericRelease/NumericReleaseItem'
import { getNonNumericReleaseStatus, getNumericReleaseStatus } from '@planning/utils/numeric-utils'
import { useTranslate } from '@tolgee/react'
import {
  getContainerAttributesParameters,
  getMultiSelectRenderBadge,
  tomTableColumnFilterPresets,
  TomTableEmptyCell,
} from '@tom-ui/ui'
import { MRT_ColumnDef, MRT_Row } from 'material-react-table'
import { ReleaseOperator } from '../Components/atoms/ReleaseOperator'
import { ReleaseStatusChip } from '../Components/atoms/ReleaseStatusChip'
import { ReleaseValidityDate } from '../Components/atoms/ReleaseValidityDate'
import { ReleaseStatus } from '../models/ReleaseStatus'
import { releaseFilterPresets } from './releaseFilterConfigs'

export const useReleaseTableColumns = <
  T extends INonNumericReleaseItem | INumericReleaseItem,
>() => {
  const { t } = useTranslate()
  const { heights } = getContainerAttributesParameters(t)

  const specificNonNumericReleaseColumns: MRT_ColumnDef<INonNumericReleaseItem>[] = [
    {
      accessorKey: 'data.archetypeName',
      header: t('archetype', 'Archetype'),
      accessorFn: row => {
        return row.data.archetypeName ?? ''
      },
      Cell: TomTableEmptyCell,
      ...releaseFilterPresets.searchMandatory(),
    },
    {
      accessorKey: 'data.containerLengths',
      header: t('length', 'Length'),
      accessorFn: row => {
        const values = row.data.containerLengths
        const mappedValues = values?.map((v: number) => `${v}ft`)
        return mappedValues ? mappedValues.join(', ') : ''
      },
      Cell: TomTableEmptyCell,
      ...releaseFilterPresets.numericMandatory(),
    },
    {
      ...releaseFilterPresets.heightMultiSelect(t),
      accessorKey: 'data.containerHeights',
      header: t('height', 'Height'),
      accessorFn: row => {
        const values = row.data.containerHeights
        const translatedValues = values?.map((v: ContainerHeight) => heights[v]?.label)
        return translatedValues ? translatedValues.join(', ') : ''
      },
      Cell: TomTableEmptyCell,
      muiFilterTextFieldProps: ({ column }) =>
        getMultiSelectRenderBadge(column, t, t('filterByHeight', 'Filter by height')),
    },
    {
      accessorKey: 'data.amount',
      header: t('quantity', 'Quantity'),
      ...releaseFilterPresets.numericMandatory(),
    },
  ]

  const commonReleaseColumnsA: MRT_ColumnDef<T>[] = [
    {
      accessorKey: 'data.shippingLine',
      header: t('operator', ' Operator'),
      Cell: ({ row }: { row: MRT_Row<T> }) => <ReleaseOperator releaseItem={row.original} />,
      ...releaseFilterPresets.operatorFilter(t),
    },
    {
      accessorKey: 'data.referenceNumber',
      size: 210,
      header: t('referenceNumber', 'Reference no'),
      ...releaseFilterPresets.searchMandatory(),
    },
    {
      ...releaseFilterPresets.statusMultiSelect(t),
      header: t('status', 'Status'),
      accessorKey: 'status',
      size: 120,
      accessorFn: row => {
        if ('amount' in row.data) {
          return getNonNumericReleaseStatus(row.data)
        } else {
          return getNumericReleaseStatus(row as INumericReleaseItem)
        }
      },
      Cell: cell => <ReleaseStatusChip status={cell.cell.getValue<ReleaseStatus>()} />,
      muiFilterTextFieldProps: ({ column }) =>
        getMultiSelectRenderBadge(column, t, t('filterByStatus', 'Filter by status')),
    },
    {
      accessorKey: 'data.modeOfTransport',
      size: 200,
      header: t('transportMode', 'Transport mode'),
      ...releaseFilterPresets.transportModeMultiSelect(t),
      Cell: TomTableEmptyCell,
      muiFilterTextFieldProps: ({ column }) =>
        getMultiSelectRenderBadge(
          column,
          t,
          t('filterByTransportMode', 'Filter by transport mode'),
        ),
    },
    {
      accessorKey: 'data.validityDate',
      header: t('validityDate', 'Validity date'),
      size: 120,
      Cell: ({ row }: { row: MRT_Row<T> }) => (
        <ReleaseValidityDate validityDate={row.original.data.validityDate} />
      ),
      ...tomTableColumnFilterPresets.dateOptional(),
    },
  ]

  const commonReleaseColumnsB: MRT_ColumnDef<T>[] = [
    {
      accessorKey: 'assigned',
      header: t('assigned', 'Assigned'),
      ...releaseFilterPresets.numericMandatory(),
    },
    {
      accessorKey: 'fulfilled',
      header: t('fulfilled', 'Fulfilled'),
      ...releaseFilterPresets.numericMandatory(),
    },
  ]

  const nonNumericReleaseColumns = [
    ...commonReleaseColumnsA,
    ...specificNonNumericReleaseColumns,
    ...commonReleaseColumnsB,
  ]
  const numericReleaseColumns = [...commonReleaseColumnsA, ...commonReleaseColumnsB]

  return { nonNumericReleaseColumns, numericReleaseColumns }
}
